import { type AuthConfig, authExchange } from '@urql/exchange-auth'
import { cacheExchange } from '@urql/exchange-graphcache'
import {
    createClient,
    type Exchange,
    fetchExchange,
    type SSRExchange,
    subscriptionExchange,
} from '@urql/next'
import {
    createClient as createWSClient,
    type SubscribePayload,
} from 'graphql-ws'
import {
    getSession,
    signOut as nextAuthSignOut,
    type SessionContextValue,
} from 'next-auth/react'
import { mutationUpdaters, subscriptionUpdaters } from '@/services/updaters'
import { clearPreferences } from '../preference/preference'

const graphQlUrl = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT
const graphQlSubscriptionsUrl =
    process.env.NEXT_PUBLIC_GRAPHQL_SUBSCRIPTIONS_ENDPOINT

async function signOut() {
    nextAuthSignOut({
        callbackUrl: window.location.origin,
    })
    await clearPreferences()
}

const authExchanges = (sessionContext: SessionContextValue): Exchange =>
    authExchange(async (utils): Promise<AuthConfig> => {
        const { data: session, status } = sessionContext

        let token = session?.user.accessToken
        let isAuthenticated = status === 'authenticated'

        return {
            addAuthToOperation(operation) {
                if (!isAuthenticated) {
                    return operation
                }

                return utils.appendHeaders(operation, {
                    Authorization: `Bearer ${token}`,
                })
            },
            didAuthError(error, operation) {
                const isAuthError = error.graphQLErrors.some(
                    (e) =>
                        e.extensions?.code === 'AUTH_NOT_AUTHORIZED' ||
                        e.extensions?.code === 'AUTH_NOT_AUTHENTICATED',
                )

                return (
                    isAuthenticated &&
                    !operation.context.skipAuthError &&
                    isAuthError
                )
            },
            async refreshAuth() {
                const newSession = await getSession()

                if (newSession) {
                    token = newSession.user.accessToken
                    isAuthenticated = true
                } else {
                    signOut()
                }
            },
        }
    })

const createUrqlClient = (
    ssr: SSRExchange,
    sessionContext: SessionContextValue,
) => {
    const wsClient =
        typeof window === 'undefined' ||
        // @ts-expect-error need to check for custom non-standard property to know if in test
        window.__vitest_environment__ === 'happy-dom'
            ? null
            : createWSClient({
                  url: graphQlSubscriptionsUrl,

                  connectionParams: async () => {
                      const isAuthenticated =
                          sessionContext.status === 'authenticated'

                      if (!isAuthenticated) return {}

                      const token = sessionContext.data.user.accessToken
                      return { authorization: `Bearer ${token}` }
                  },
              })

    const client = createClient({
        url: graphQlUrl,
        requestPolicy: 'cache-and-network',
        exchanges: [
            cacheExchange({
                keys: {
                    MeetplaceTagType: (data) => data.tagId as string,
                    GroupType: (data) => data.id as string,
                    AppMetaData: (data) => data.name as string,
                    GetMeetplaceQuotaOverviewPayload: () => null,
                    Status: (data) => data.name as string,
                    BaseUser: (data) => data.userId as string,
                    MeetingContent: (data) => data.meetingId as string,
                    ConfluenceSitesDTO: (data) => data.siteId as string,
                    Subscription_BaseUser: (data) => data.userId as string,
                    Reaction: () => null,
                    ViewGroupPayload: () => null,
                    ViewMeetingPayload: () => null,
                    ActionsClusteredPayload: () => null,
                    TeamsSettings: () => null,
                    ZoomSettings: () => null,
                    MemberType: () => null,
                    AnalyticsWidget: () => null,
                    NotificationSettings: () => null,
                    CalendarPreferences: () => null,
                    MeetplaceOwnerInfo: () => null,
                    LicenseOutput: () => null,
                    MemberRolesCounts: () => null,
                    AppDirectory: () => null,
                    GroupMeetingsDashboard: () => null,
                    GroupInsightsDashboard: () => null,
                    GroupActionsDashboard: () => null,
                    AttendeeType: () => null,
                    Conference: () => null,
                    RecurrencePattern: () => null,
                    PendingItemsCounter: () => null,
                    ActionStatus: () => null,
                    AssigneeType: () => null,
                    ApproverType: () => null,
                    JiraLinkage: () => null,
                    OwnerType: () => null,
                    SyncStatusPayload: () => null,
                    ActionsMeetplaceInsights: () => null,
                    MeetingInsights: () => null,
                    UsersInsights: () => null,
                    MeetingsChartsElement: () => null,
                    GroupsChartElement: () => null,
                    ContentItemsChartElement: () => null,
                    MeetingRoomContent: () => null,
                    CalendarsCollectionSegment: () => null,
                    CollectionSegmentInfo: () => null,
                    CheckAttendeeEmailPayload: () => null,
                    ConfluenceSettings: () => null,
                    EntraIDUserPayload: () => null,
                    MeetplaceSAMLConfig: () => null,
                    CalendarResponse: () => null,
                    IAdamPreferencesPayload: () => null,
                    RequesterType: () => null,
                    user_NotificationSettings: () => null,
                },
                updates: {
                    Mutation: mutationUpdaters,
                    Subscription: subscriptionUpdaters,
                },
            }),
            authExchanges(sessionContext),
            ssr,
            fetchExchange,
            subscriptionExchange({
                forwardSubscription(operation) {
                    return {
                        subscribe: (sink) => {
                            // if we're server-side, we don't want to subscribe
                            if (wsClient === null) {
                                return { unsubscribe: () => {} }
                            }

                            const dispose = wsClient.subscribe(
                                operation as SubscribePayload,
                                sink,
                            )

                            return {
                                unsubscribe: dispose,
                            }
                        },
                    }
                },
            }),
        ],
        suspense: false,
    })

    return client
}

export { createUrqlClient }
